/*
 * Shared-layout source mapping helpers: link checks and writes, the shared-CSS range, exact splices and the
 * region/slot map of a full layout document.
 *
 * Ported from the approved prototype hs-base/v2-source.js (219 lines):
 *   lines 21-49   VOID, TOKEN_RE, KIND_LABEL, lineOf, decode, encode, attr, excerpt   (internal helpers)
 *   lines 51-86   segment                                                              (internal; used by mapLayout)
 *   lines 88-128  linksOf, decAttr, escAttr, linkTextProblem, linkHrefProblem, setLinkText, setLinkHref
 *   lines 129-149 classify                                                             (internal; used by segment)
 *   lines 158-175 mapLayout, splice
 *   lines 198-205 cssRange
 * Behaviour, strings and return shapes are unchanged, with one recorded deviation: the example address in
 * linkHrefProblem's host-name message is neutral (https://www.example.com/contact) instead of a customer domain.
 * Pure: no DOM, no window, no storage.
 */

const VOID: Record<string, 1> = { area: 1, base: 1, br: 1, col: 1, embed: 1, hr: 1, img: 1, input: 1, link: 1, meta: 1, param: 1, source: 1, track: 1, wbr: 1 };
/* Module-level global regex: segment() resets lastIndex to 0 before every scan, exactly as the prototype. */
const TOKEN_RE = /\{%-?[^\n]*?-?%\}|\{\{-?[^{}<\n]*?-?\}\}|<!--[\s\S]*?-->|<\/?[a-zA-Z][\w:-]*(?:\s+[^\s"'>\/=]+(?:\s*=\s*(?:"[^"]*"|'[^']*'|[^\s"'=<>`]+))?)*\s*\/?>/g;
const KIND_LABEL: Record<string, string> = { heading: "Heading", text: "Paragraph", note: "Small note", logo: "Logo text", button: "Button" };

/* ---------- result shapes ---------- */

export interface SourceLink {
  text: string;
  textStart: number;
  textEnd: number;
  href: string;
  rawHref: string;
  hrefStart: number;
  hrefEnd: number;
  line: number;
}
export interface SourceProtectedItem { kind: "protected"; label: string; start: number; end: number; summary: string; line: number }
export interface SourceLogicItem { kind: "logic"; start: number; end: number; summary: string; line: number }
export interface SourceBlockItem {
  kind: "block";
  type: string;
  label: string;
  tag: string;
  start: number;
  end: number;
  innerStart: number;
  innerEnd: number;
  text: string;
  line: number;
  href?: { value: string; field: string | null; start: number; end: number } | null;
}
export interface SourceLinksItem { kind: "links"; label: string; start: number; end: number; links: SourceLink[]; line: number }
export type SourceItem = SourceProtectedItem | SourceLogicItem | SourceBlockItem | SourceLinksItem;

export interface SegmentResult { ok: boolean; reason?: string; items: SourceItem[] }
export interface LayoutRegionMap { name: string; ok: boolean; reason: string | null; items: SourceItem[]; insertAt: number }
export interface LayoutMap { regions: LayoutRegionMap[]; slot: { start: number; end: number; line: number } | null }
export interface CssRange { start: number; end: number; css: string }

/* ---------- internal helpers ---------- */

export function lineOf(src: string, i: number): number { return src.slice(0, i).split("\n").length; }
function decode(s: unknown): string {
  return String(s).replace(/\s*\n\s*/g, " ").replace(/<br\s*\/?>/gi, "\n")
    .replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, "\"").replace(/&#39;/g, "'").replace(/&nbsp;/g, "\u00a0").replace(/&amp;/g, "&");
}
/* Plain text back to HTML: escape markup, keep {{ field }} outputs verbatim, newline = <br>. */
function encode(text: unknown): string {
  const parts = String(text).split(/(\{\{[^{}<\n]*?\}\})/);
  return parts.map((p, i) => {
    if (i % 2 === 1) return p;
    return p.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br>");
  }).join("");
}
interface AttrSpan { value: string; start: number; end: number }
function attr(openTag: string, name: string): AttrSpan | null {
  const re = new RegExp("\\s" + name + "\\s*=\\s*(\"([^\"]*)\"|'([^']*)')", "i");
  const m = re.exec(openTag);
  if (!m) return null;
  const valueStart = m.index + m[0].indexOf(m[1] as string) + 1;
  const value = (m[2] != null ? m[2] : m[3]) as string;
  return { value, start: valueStart, end: valueStart + value.length };
}
function excerpt(html: unknown): string {
  const t = String(html).replace(/\{%[\s\S]*?%\}/g, " ").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  return t.length > 90 ? t.slice(0, 87) + "..." : t;
}

/* Split [from, to) of src into top-level items. */
function segment(src: string, from: number, to: number): SegmentResult {
  const items: SourceItem[] = []; const stack: string[] = []; let elStart = -1; let elOpenEnd = -1; let lastEnd = from; let m: RegExpExecArray | null;
  const chunk = src.slice(from, to);
  TOKEN_RE.lastIndex = 0;
  const pushText = (a: number, b: number): void => {
    const t = src.slice(a, b);
    if (t.trim()) items.push({ kind: "protected", label: "Text", start: a, end: b, summary: excerpt(t), line: lineOf(src, a) });
  };
  while ((m = TOKEN_RE.exec(chunk))) {
    const s = from + m.index; const e = s + m[0].length; const tok = m[0];
    if (stack.length === 0 && s > lastEnd) pushText(lastEnd, s);
    if (tok.indexOf("{%") === 0) {
      if (!stack.length) items.push({ kind: "logic", start: s, end: e, summary: tok.replace(/^\{%-?\s*|\s*-?%\}$/g, ""), line: lineOf(src, s) });
    } else if (tok.indexOf("{{") === 0) {
      if (!stack.length) items.push({ kind: "protected", label: "Output", start: s, end: e, summary: tok, line: lineOf(src, s) });
    } else if (tok.indexOf("<!--") === 0) {
      /* comments stay in source; not shown */
    } else if (tok.charAt(1) === "/") {
      const name = tok.slice(2).replace(/[\s>].*$/, "").toLowerCase();
      if (!stack.length || stack[stack.length - 1] !== name) return { ok: false, reason: "Unbalanced </" + name + "> on line " + lineOf(src, s), items: [] };
      stack.pop();
      if (!stack.length) items.push(classify(src, elStart, elOpenEnd, s, e));
    } else {
      const nm = tok.slice(1).replace(/[\s\/>].*$/, "").toLowerCase();
      const selfClose = /\/>$/.test(tok) || VOID[nm];
      if (!stack.length) { elStart = s; elOpenEnd = e; }
      if (selfClose) { if (!stack.length) items.push(classify(src, s, e, e, e)); }
      else stack.push(nm);
    }
    if (!stack.length) lastEnd = e;
  }
  if (stack.length) return { ok: false, reason: "<" + stack[stack.length - 1] + "> is not closed", items: [] };
  if (to > lastEnd) pushText(lastEnd, to);
  return { ok: true, items };
}

/* ---------- links ---------- */

/* Only <a data-edit="link" href="..."> with plain text inside, separated by plain text; anything else is not a links paragraph. */
export function linksOf(src: string, from: number, to: number): SourceLink[] | null {
  const inner = src.slice(from, to), re = /<a\s([^<>]*)>([^<>{}]*)<\/a>/g; let m: RegExpExecArray | null, last = 0; const out: SourceLink[] = [];
  while ((m = re.exec(inner))) {
    if (/[<>{}]/.test(inner.slice(last, m.index))) return null;
    const open = "<a " + m[1] + ">"; const ed = attr(open, "data-edit"), hr = attr(open, "href");
    if (!ed || ed.value !== "link" || !hr || /[<>{}]/.test(hr.value)) return null;
    const text = m[2] as string;
    const aStart = from + m.index, openLen = m[0].length - text.length - 4;
    out.push({ text: decode(text), textStart: aStart + openLen, textEnd: aStart + openLen + text.length, href: decAttr(hr.value), rawHref: hr.value, hrefStart: aStart + hr.start, hrefEnd: aStart + hr.end, line: lineOf(src, aStart) });
    last = m.index + m[0].length;
  }
  if (!out.length || /[<>{}]/.test(inner.slice(last))) return null;
  return out;
}
const NAMED: Record<string, string> = { amp: "&", lt: "<", gt: ">", quot: "\"", apos: "'", nbsp: "\u00a0" };
/* Attribute value -> text, decoded exactly once (named amp/lt/gt/quot/apos/nbsp and numeric references). Writes use the raw span offsets. */
export function decAttr(s: unknown): string {
  return String(s).replace(/&(amp|lt|gt|quot|apos|nbsp|#(\d{1,7})|#x([0-9a-f]{1,6}));/gi, (m: string, n: string, d: string | undefined, x: string | undefined) => {
    if (d || x) { const c = d ? parseInt(d, 10) : parseInt(x as string, 16); return c > 0 && c <= 0x10ffff ? String.fromCodePoint(c) : m; }
    return NAMED[n.toLowerCase()] as string;
  });
}
export function escAttr(s: unknown): string { return String(s).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;"); }
/* Link text: one line of plain text (no placeholders, Liquid or markup). */
export function linkTextProblem(t: unknown): string | null {
  const s = String(t == null ? "" : t);
  if (!s.trim()) return "Link text can't be empty.";
  if (/[\u0000-\u001f\u007f]/.test(s)) return "Link text is one line of plain text.";
  if (/[{}]/.test(s)) return "Link text is plain text: placeholders and Liquid aren't allowed here.";
  if (/[<>]/.test(s)) return "Link text is plain text: HTML isn't allowed here.";
  if (s.length > 80) return "Keep link text under 80 characters.";
  return null;
}
/* Link address: a full https:// URL with a host name; no spaces, control characters, quotes, placeholders or Liquid. */
export function linkHrefProblem(u: unknown): string | null {
  const s = String(u == null ? "" : u);
  if (!s) return "Add the link address (https://…).";
  if (/[\s\u0000-\u001f\u007f]/.test(s)) return "A link address can't contain spaces or line breaks.";
  if (/["'<>{}\\^`|]/.test(s)) return "A link address can't contain quotes, brackets, placeholders or Liquid.";
  if (!/^https:\/\//i.test(s)) return "Use a full https:// address.";
  const host = /^https:\/\/([^\/?#:@]+)(:\d{1,5})?(?:[\/?#]|$)/i.exec(s);
  /* DEVIATION (recorded): the prototype's example named a customer domain; the example is neutral here. */
  if (!host || !/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/i.test(host[1] as string) || !/\.[a-z]{2,}$/i.test(host[1] as string)) return "Use a full https:// address with a host name, like https://www.example.com/contact.";
  if (s.length > 500) return "Keep the link address under 500 characters.";
  return null;
}
export function setLinkText(src: string, link: Pick<SourceLink, "textStart" | "textEnd">, text: unknown): string { return linkTextProblem(text) ? src : splice(src, link.textStart, link.textEnd, encode(String(text))); }
export function setLinkHref(src: string, link: Pick<SourceLink, "hrefStart" | "hrefEnd">, url: unknown): string { return linkHrefProblem(url) ? src : splice(src, link.hrefStart, link.hrefEnd, escAttr(url)); }

function classify(src: string, start: number, openEnd: number, closeStart: number, end: number): SourceItem {
  const open = src.slice(start, openEnd);
  const tag = open.slice(1).replace(/[\s\/>].*$/, "").toLowerCase();
  const inner = src.slice(openEnd, closeStart);
  const edit = attr(open, "data-edit");
  const simple = inner.replace(/<br\s*\/?>/gi, "").indexOf("<") === -1 && inner.indexOf("{%") === -1;
  const line = lineOf(src, start);
  if (edit && KIND_LABEL[edit.value] && simple) {
    const item: SourceBlockItem = { kind: "block", type: edit.value, label: KIND_LABEL[edit.value] as string, tag, start, end, innerStart: openEnd, innerEnd: closeStart, text: decode(inner), line };
    if (edit.value === "button") {
      const href = attr(open, "href");
      const fm = href && /^\{\{\s*([\w.]+)\s*\}\}$/.exec(href.value);
      item.href = href ? { value: href.value, field: fm ? (fm[1] as string) : null, start: start + href.start, end: start + href.end } : null;
    }
    return item;
  }
  if (tag === "p" && !edit) { const lk = linksOf(src, openEnd, closeStart); if (lk) return { kind: "links", label: "Links", start, end, links: lk, line }; }
  const why = inner.indexOf("{%") !== -1 ? "contains logic" : (edit ? "contains nested markup" : "custom markup");
  const NAMES: Record<string, string> = { table: "Table", ul: "List", ol: "List", img: "Image", hr: "Divider", div: "Block", a: "Link" };
  const name = NAMES[tag] || ("<" + tag + ">");
  return { kind: "protected", label: name + " (" + why + ")", start, end, summary: excerpt(src.slice(start, end)), line };
}

/* ---------- layout map, splices, shared CSS ---------- */

/* Full layout document -> regions + slot. */
export function mapLayout(input: unknown): LayoutMap {
  const src = String(input || "");
  const regions: LayoutRegionMap[] = []; const re = /<!--\s*region:([\w-]+)\s*-->/g; let m: RegExpExecArray | null;
  while ((m = re.exec(src))) {
    const name = m[1] as string; const innerStart = m.index + m[0].length;
    const closeRe = new RegExp("<!--\\s*\\/region:" + name + "\\s*-->", "g");
    closeRe.lastIndex = innerStart;
    const c = closeRe.exec(src);
    if (!c) { regions.push({ name, ok: false, reason: "The region:" + name + " marker has no closing marker", items: [], insertAt: innerStart }); continue; }
    const seg = segment(src, innerStart, c.index);
    regions.push({ name, ok: seg.ok, reason: seg.reason || null, items: seg.items, insertAt: c.index });
  }
  const sm = /\{\{-?\s*body\s*-?\}\}/.exec(src);
  return { regions, slot: sm ? { start: sm.index, end: sm.index + sm[0].length, line: lineOf(src, sm.index) } : null };
}

export function splice(src: string, start: number, end: number, text: string): string { return src.slice(0, start) + text + src.slice(end); }

/* The CSS tab edits the inside of <style data-shared-css> only. */
export function cssRange(src: string | null | undefined): CssRange | null {
  const m = /<style[^>]*data-shared-css[^>]*>/i.exec(src || "");
  if (!m || src == null) return null;
  const start = m.index + m[0].length; const end = src.indexOf("</style>", start);
  if (end === -1) return null;
  return { start, end, css: src.slice(start, end) };
}
